/**
 * Architecture Diagram Component
 *
 * Card-based visual showing the flow:
 * Agents -> Worker -> Durable Object
 */

export default function ArchitectureDiagram() {
  return (
    <section className="mt-10 md:mt-16 w-full max-w-full overflow-x-hidden">
      <h2 className="text-2xl font-bold text-center mb-8">How It Works</h2>

      {/* Desktop: 5-column grid (card, arrow, card, arrow, card) */}
      <div className="hidden md:grid md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-4 items-center">
        {/* Agent Box - stacked to show multiple agents */}
        <div className="stack stack-bottom">
          <div className="card bg-amber-100 shadow-md">
            <div className="card-body p-4">
              <h3 className="card-title text-lg text-amber-900">Agents</h3>
              <div className="space-y-2 text-sm text-amber-800">
                <div className="flex items-center gap-2">
                  <span className="badge badge-ghost bg-amber-200 text-amber-900">CLI</span>
                  <span className="badge badge-ghost bg-amber-200 text-amber-900">Web</span>
                </div>
                <p className="font-mono text-xs bg-amber-50 rounded px-2 py-1">
                  Ed25519 Identity
                </p>
                <p className="text-amber-700">Sign claims locally</p>
              </div>
            </div>
          </div>
          <div className="card bg-amber-200/80 shadow-sm h-full"></div>
          <div className="card bg-amber-300/60 shadow-sm h-full"></div>
        </div>

        {/* Arrow 1 */}
        <div className="flex flex-col items-center gap-1 px-2">
          <div className="flex items-center">
            <div className="w-8 h-0.5 bg-base-content/30"></div>
            <div className="w-0 h-0 border-t-4 border-b-4 border-l-6 border-t-transparent border-b-transparent border-l-base-content/30"></div>
          </div>
          <span className="text-xs text-base-content/50 whitespace-nowrap">WebSocket</span>
          <div className="flex items-center">
            <div className="w-0 h-0 border-t-4 border-b-4 border-r-6 border-t-transparent border-b-transparent border-r-base-content/30"></div>
            <div className="w-8 h-0.5 bg-base-content/30"></div>
          </div>
        </div>

        {/* Worker Box */}
        <div className="card bg-violet-100 shadow-md">
          <div className="card-body p-4">
            <h3 className="card-title text-lg text-violet-900">Worker</h3>
            <div className="space-y-2 text-sm text-violet-800">
              <p className="font-mono text-xs bg-violet-50 rounded px-2 py-1">
                Capability URLs
              </p>
              <p className="text-violet-700">Verify signatures</p>
              <p className="text-violet-700">Route to plan</p>
            </div>
          </div>
        </div>

        {/* Arrow 2 */}
        <div className="flex flex-col items-center gap-1 px-2">
          <div className="flex items-center">
            <div className="w-8 h-0.5 bg-base-content/30"></div>
            <div className="w-0 h-0 border-t-4 border-b-4 border-l-6 border-t-transparent border-b-transparent border-l-base-content/30"></div>
          </div>
          <span className="text-xs text-base-content/50 whitespace-nowrap">Durable RPC</span>
          <div className="flex items-center">
            <div className="w-0 h-0 border-t-4 border-b-4 border-r-6 border-t-transparent border-b-transparent border-r-base-content/30"></div>
            <div className="w-8 h-0.5 bg-base-content/30"></div>
          </div>
        </div>

        {/* Durable Object Box */}
        <div className="card bg-emerald-100 shadow-md">
          <div className="card-body p-4">
            <h3 className="card-title text-lg text-emerald-900">Durable Object</h3>
            <div className="space-y-2 text-sm text-emerald-800">
              <p className="font-mono text-xs bg-emerald-50 rounded px-2 py-1">
                Append-Only Log
              </p>
              <p className="text-emerald-700">Store claims</p>
              <p className="text-emerald-700">Broadcast updates</p>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile: vertical stack */}
      <div className="md:hidden flex flex-col items-center gap-2">
        {/* Agent Box - stacked */}
        <div className="stack stack-bottom w-full max-w-xs">
          <div className="card bg-amber-100 shadow-md">
            <div className="card-body p-4">
              <h3 className="card-title text-lg text-amber-900">Agents</h3>
              <div className="space-y-2 text-sm text-amber-800">
                <div className="flex items-center gap-2">
                  <span className="badge badge-ghost bg-amber-200 text-amber-900">CLI</span>
                  <span className="badge badge-ghost bg-amber-200 text-amber-900">Web</span>
                </div>
                <p className="font-mono text-xs bg-amber-50 rounded px-2 py-1">
                  Ed25519 Identity
                </p>
              </div>
            </div>
          </div>
          <div className="card bg-amber-200/80 shadow-sm h-full"></div>
          <div className="card bg-amber-300/60 shadow-sm h-full"></div>
        </div>

        {/* Arrow down */}
        <div className="flex flex-col items-center py-1">
          <div className="w-0.5 h-4 bg-base-content/30"></div>
          <div className="w-0 h-0 border-l-4 border-r-4 border-t-6 border-l-transparent border-r-transparent border-t-base-content/30"></div>
          <span className="text-xs text-base-content/50 mt-1">WebSocket</span>
        </div>

        {/* Worker Box */}
        <div className="card bg-violet-100 shadow-md w-full max-w-xs">
          <div className="card-body p-4">
            <h3 className="card-title text-lg text-violet-900">Worker</h3>
            <div className="space-y-2 text-sm text-violet-800">
              <p className="font-mono text-xs bg-violet-50 rounded px-2 py-1">
                Capability URLs
              </p>
              <p className="text-violet-700">Verify signatures</p>
            </div>
          </div>
        </div>

        {/* Arrow down */}
        <div className="flex flex-col items-center py-1">
          <div className="w-0.5 h-4 bg-base-content/30"></div>
          <div className="w-0 h-0 border-l-4 border-r-4 border-t-6 border-l-transparent border-r-transparent border-t-base-content/30"></div>
          <span className="text-xs text-base-content/50 mt-1">Durable RPC</span>
        </div>

        {/* Durable Object Box */}
        <div className="card bg-emerald-100 shadow-md w-full max-w-xs">
          <div className="card-body p-4">
            <h3 className="card-title text-lg text-emerald-900">Durable Object</h3>
            <div className="space-y-2 text-sm text-emerald-800">
              <p className="font-mono text-xs bg-emerald-50 rounded px-2 py-1">
                Append-Only Log
              </p>
              <p className="text-emerald-700">Broadcast updates</p>
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap justify-center gap-4 mt-6 text-sm text-base-content/60">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-amber-100"></div>
          <span>Client</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-violet-100"></div>
          <span>Edge</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-emerald-100"></div>
          <span>Stateful</span>
        </div>
        <div className="flex items-center gap-2 text-base-content/40">
          <span>·</span>
          <span>~32k simultaneous agents</span>
        </div>
      </div>
    </section>
  );
}
